import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

/**
 * Hash-anchor landing for long pages: wait for webfonts (layout settles),
 * then align the target once with an instant scroll honoring scroll-margin.
 * Any manual scroll input while waiting aborts the alignment; hash changes
 * and unmounts cancel pending work. No timers or repeated scrolling.
 */
export const useHashAnchor = (): void => {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash || hash.length < 2) return;
    let id: string;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      return;
    }
    if (!id) return;

    let done = false;
    let raf = 0;
    const abort = () => {
      done = true;
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
      cleanup();
    };
    const onKey = (e: KeyboardEvent) => {
      if (SCROLL_KEYS.has(e.key)) abort();
    };
    const cleanup = () => {
      window.removeEventListener('wheel', abort);
      window.removeEventListener('touchmove', abort);
      window.removeEventListener('pointerdown', abort);
      window.removeEventListener('keydown', onKey);
    };

    window.addEventListener('wheel', abort, { passive: true });
    window.addEventListener('touchmove', abort, { passive: true });
    window.addEventListener('pointerdown', abort, { passive: true });
    window.addEventListener('keydown', onKey);

    const fonts = typeof document !== 'undefined' ? (document as Document).fonts : undefined;
    const ready: Promise<unknown> = fonts?.ready ? fonts.ready.catch(() => undefined) : Promise.resolve();
    ready.then(() => {
      if (done) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (done) return;
        document.getElementById(id)?.scrollIntoView({ behavior: 'auto', block: 'start' });
        done = true;
        cleanup();
      });
    });

    return () => {
      done = true;
      if (raf) cancelAnimationFrame(raf);
      cleanup();
    };
  }, [hash]);
};

export default useHashAnchor;
